import { useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import GetStartedModal from "../../../components/feature/GetStartedModal";

export default function DemoHero() {
  const { isDark } = useTheme();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {showModal && <GetStartedModal onClose={() => setShowModal(false)} />}
      <section className={`relative pt-32 pb-20 px-8 lg:px-16 overflow-hidden transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-white"}`}>
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full blur-3xl pointer-events-none ${isDark ? "bg-white/[0.02]" : "bg-black/[0.02]"}`} />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex justify-center mb-8">
            <div className={`inline-flex items-center gap-2 border rounded-full px-4 py-1.5 ${isDark ? "bg-white/5 border-white/10" : "bg-black/4 border-black/8"}`}>
              <i className={`ri-calculator-line text-sm ${isDark ? "text-[#888888]" : "text-[#555555]"}`} />
              <span className={`font-inter text-xs uppercase tracking-widest ${isDark ? "text-[#888888]" : "text-[#555555]"}`}>
                How Trackly Works
              </span>
            </div>
          </div>

          <div className="text-center mb-6">
            <h1 className={`font-syne font-bold text-6xl lg:text-7xl xl:text-[78px] leading-[1.0] mb-3 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Four steps to
            </h1>
            <h2 className={`font-syne font-bold text-6xl lg:text-7xl xl:text-[78px] text-transparent bg-clip-text leading-[1.05] ${isDark ? "bg-gradient-to-r from-[#aaaaaa] to-[#333333]" : "bg-gradient-to-r from-[#555555] to-[#dddddd]"}`}>
              real margins.
            </h2>
          </div>

          <p className={`font-inter text-lg text-center max-w-xl mx-auto mb-12 leading-relaxed ${isDark ? "text-[#555555]" : "text-[#777777]"}`}>
            No spreadsheets. No guessing. Enter your numbers and Trackly shows exactly what you made — whether you&apos;re selling cards, sneakers, clothes, or anything in between.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <button
              onClick={() => setShowModal(true)}
              className={`font-inter font-medium text-sm px-8 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${isDark ? "bg-white text-black hover:bg-[#e8e8e8]" : "bg-[#111111] text-white hover:bg-[#2a2a2a]"}`}
            >
              Get Early Access
              <i className="ri-arrow-right-line" />
            </button>
            <a href="#steps">
              <button className={`font-inter text-sm border px-8 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${isDark ? "text-white border-white/15 hover:border-white/30 hover:bg-white/5" : "text-[#111111] border-black/15 hover:border-black/30 hover:bg-black/5"}`}>
                <i className="ri-arrow-down-line" />
                See the Walkthrough
              </button>
            </a>
          </div>

          {/* Visual overview card */}
          <div className={`border rounded-3xl p-8 lg:p-12 ${isDark ? "bg-[#0d0d0d] border-white/8" : "bg-[#f0f0f0] border-black/8"}`}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { step: "01", label: "Enter your buy price", icon: "ri-price-tag-3-line" },
                { step: "02", label: "Choose your platform", icon: "ri-store-2-line" },
                { step: "03", label: "Add shipping costs", icon: "ri-truck-line" },
                { step: "04", label: "See your real profit", icon: "ri-money-dollar-circle-line" },
              ].map((s, idx) => (
                <div key={s.step} className="relative">
                  <div className={`border rounded-xl p-5 text-center ${isDark ? "bg-[#111111] border-white/5" : "bg-white border-black/5"}`}>
                    <div className={`w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-lg ${isDark ? "bg-white/5" : "bg-black/5"}`}>
                      <i className={`${s.icon} text-lg ${isDark ? "text-[#555555]" : "text-[#999999]"}`} />
                    </div>
                    <p className={`font-syne font-bold text-3xl mb-1 ${isDark ? "text-[#282828]" : "text-[#e0e0e0]"}`}>{s.step}</p>
                    <p className={`font-inter text-xs leading-snug ${isDark ? "text-[#555555]" : "text-[#777777]"}`}>{s.label}</p>
                  </div>
                  {idx < 3 && (
                    <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 items-center justify-center z-10">
                      <i className={`ri-arrow-right-line text-sm ${isDark ? "text-[#333333]" : "text-[#cccccc]"}`} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Platform tag cloud */}
            <div className={`border-t pt-6 flex flex-wrap gap-2 justify-center ${isDark ? "border-white/5" : "border-black/5"}`}>
              {["eBay", "StockX", "GOAT", "Poshmark", "Whatnot", "Mercari", "Depop", "Facebook Marketplace", "Amazon", "OfferUp"].map((p) => (
                <span key={p} className={`font-inter text-xs border rounded-full px-3 py-1 ${isDark ? "text-[#555555] bg-white/3 border-white/6" : "text-[#888888] bg-black/4 border-black/6"}`}>
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
